import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Archive, CheckCheck, RotateCcw } from 'lucide-react'
import { isLiabilityType, isPlainAccount, type AccountSummary } from '@/api/accounts'
import { getEarmarks } from '@/api/goals'
import { listPersonalLoans } from '@/api/personalLoans'
import { Button } from '@/components/ui/button'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { investmentGain, subTypeLabel } from '@/lib/accountDisplay'
import { getDefaultAccountIcon } from '@/lib/accountIconOptions'
import { CategoryIcon } from '@/lib/categoryIcon'
import { nextBillDate } from '@/lib/creditCard'
import { PERSONAL_LOANS_ENABLED } from '@/lib/featureFlags'
import { formatShortDate } from '@/lib/formatDate'
import { ROUTES } from '@/routes/routes'

const SHORT_DATE = { day: 'numeric', month: 'short' } as const

function useEarmarks(account: AccountSummary) {
  return useQuery({
    queryKey: ['earmarks', account.id],
    queryFn: () => getEarmarks(account.id),
    enabled: isPlainAccount(account.type) && !account.archivedAt,
  })
}

function usePersonalLoanContact(account: AccountSummary): string | null {
  const enabled =
    PERSONAL_LOANS_ENABLED && (account.type === 'RECEIVABLE' || account.type === 'PAYABLE')
  const { data } = useQuery({
    queryKey: ['personal-loans', 'all'],
    queryFn: () => listPersonalLoans(),
    enabled,
  })
  return (enabled && data?.find((l) => l.accountId === account.id)?.contact.name) || null
}

export function AccountRow({
  account,
  ownerNames,
  emiAmount,
  onArchive,
}: {
  account: AccountSummary
  /** Null when the household has a single member — names would just repeat it on every row. */
  ownerNames: string | null
  /** LOAN only; null when the loan has no EMI set (or hasn't loaded). */
  emiAmount?: number | null
  onArchive: (account: AccountSummary) => void
}) {
  const format = useListAmountFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const { data: earmarks } = useEarmarks(account)
  const loanContact = usePersonalLoanContact(account)
  const liability = isLiabilityType(account.type)
  const archived = !!account.archivedAt
  const gain = investmentGain(account)
  const reconciliation = archived ? null : account.reconciliation

  const details: React.ReactNode[] = []
  let meter: number | null = null
  let caption: React.ReactNode = null

  if (account.subType) details.push(subTypeLabel(account.subType))
  if (account.type === 'CREDIT_CARD' && account.creditLimit) {
    const used = Math.abs(account.balance) / account.creditLimit
    meter = Math.min(used, 1)
    details.push(`${Math.round(used * 100)}% of ${format(account.creditLimit)} limit used`)
    const bill = nextBillDate(account.billingDate, new Date())
    if (bill) details.push(`bill ${bill.toLocaleDateString('en-IN', SHORT_DATE)}`)
  }
  if (account.type === 'LOAN') details.push(emiAmount ? `EMI ${format(emiAmount)}` : 'Loan')
  if (loanContact) details.push(`Personal loan · ${loanContact}`)
  if (earmarks && earmarks.earmarked > 0) {
    details.push(
      <span key="earmark">
        <span className="font-medium text-[var(--fl-warning)]">
          {format(earmarks.earmarked)} for goals
        </span>
        {' · '}
        {format(earmarks.available)} free
      </span>
    )
  }
  if (ownerNames) details.push(ownerNames)

  if (account.type === 'CREDIT_CARD' || account.type === 'PAYABLE') caption = 'to pay'
  if (account.type === 'LOAN') caption = 'left to pay'
  if (gain) {
    const sign = gain.amount >= 0 ? '+' : '−'
    caption = (
      <span className={gain.amount >= 0 ? 'text-[var(--fl-income)]' : 'text-[var(--fl-expense)]'}>
        {sign}
        {format(Math.abs(gain.amount))}
        {gain.pct !== null && ` · ${Math.abs(gain.pct).toFixed(1)}%`}
      </span>
    )
  }

  const amount = gain
    ? account.currentValue!
    : liability
      ? Math.abs(account.balance)
      : account.balance

  return (
    <li className="border-b border-[var(--fl-divider)] last:border-b-0">
      <div
        role="link"
        tabIndex={0}
        aria-label={account.name}
        onClick={() =>
          navigate(
            account.type === 'LOAN'
              ? ROUTES.APP.LOAN_DETAIL(account.id)
              : ROUTES.APP.ACCOUNT_DETAIL(account.id)
          )
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.click()
        }}
        className={`group flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-[var(--fl-surface-hover)] md:gap-3 md:px-4 md:py-3 ${archived ? 'opacity-60' : ''}`}
      >
        <CategoryIcon
          icon={account.icon || getDefaultAccountIcon(account.type, account.subType)}
          size={wide ? 17 : 15}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)] md:h-9 md:w-9 md:rounded-xl"
        />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-[var(--fl-heading)] md:text-base">
              {account.name}
            </span>
            {archived && (
              <span className="shrink-0 rounded-full bg-[var(--fl-surface-subtle)] px-2 py-0.5 text-[11px] font-medium text-[var(--fl-text-2)]">
                Archived
              </span>
            )}
            {reconciliation?.status === 'RECONCILED' && reconciliation.newSince === 0 && (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-[var(--fl-primary-subtle)] px-2 py-0.5 text-[11px] font-medium text-[var(--fl-primary-text)]">
                <CheckCheck size={11} aria-hidden="true" /> Reconciled ·{' '}
                {formatShortDate(reconciliation.asOf)}
              </span>
            )}
            {reconciliation?.status === 'RECHECK' && (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-[var(--fl-warning-bg)] px-2 py-0.5 text-[11px] font-medium text-[var(--fl-warning-dark)]">
                <RotateCcw size={11} aria-hidden="true" /> Re-check
              </span>
            )}
          </p>
          {details.length > 0 && (
            <p className="mt-0.5 truncate text-[11.5px] text-[var(--fl-text-3)] md:text-xs">
              {details.map((d, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  {d}
                </span>
              ))}
            </p>
          )}
          {meter !== null && (
            <div className="mt-1.5 h-1 max-w-[200px] overflow-hidden rounded-full bg-[var(--fl-surface-subtle)]">
              <div
                className="h-full rounded-full bg-[var(--fl-expense)] opacity-75"
                style={{ width: `${Math.round(meter * 100)}%` }}
              />
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p
            className={`font-serif text-[15px] font-semibold tabular-nums md:text-[17px] ${liability ? 'text-[var(--fl-expense)]' : 'text-[var(--fl-heading)]'}`}
          >
            {format(amount)}
          </p>
          {caption && <p className="text-[11px] text-[var(--fl-text-3)]">{caption}</p>}
        </div>
        {!isReadOnly && !archived && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={(e) => {
              e.stopPropagation()
              onArchive(account)
            }}
            tooltip="Archive account"
            aria-label="Archive account"
            className="hidden text-[var(--fl-text-3)] opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[var(--fl-error-bg)] hover:text-[var(--fl-error)] md:inline-flex"
          >
            <Archive size={14} />
          </Button>
        )}
      </div>
    </li>
  )
}
